export type FilterRow = {
  id: string;
  search: string;
  facets: Record<string, string | undefined>;
  facetLabels?: Record<string, string | undefined>;
  dates: Record<string, string | undefined>;
  sort: Record<string, string | number | undefined>;
};
export type ListQuery = {
  keyword: string;
  selected: Record<string, string[]>;
  date: { field: string; mode: 'ALL' | 'RANGE' | 'MISSING'; from?: string; to?: string };
  sort: { field: string; direction: 'asc' | 'desc' };
  groupBy: string;
};
export type ListResult = { ids: string[]; total: number; groups: { key: string; ids: string[] }[]; error?: string };

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Shanghai',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});
function validCalendar(value: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
export function beijingDay(value: string | undefined): string | undefined {
  if (!value || !validCalendar(value.slice(0, 10))) return undefined;
  if (value.length === 10) return value;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return undefined;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return undefined;
  const parts = dayFormatter.formatToParts(date);
  return ['year', 'month', 'day'].map((type) => parts.find((part) => part.type === type)?.value).join('-');
}

export function runListQuery(rows: readonly FilterRow[], query: ListQuery): ListResult {
  const { date } = query;
  if (
    date.mode === 'RANGE' &&
    (!date.from || !date.to || !validCalendar(date.from) || !validCalendar(date.to) || date.from > date.to)
  ) {
    return { ids: [], total: 0, groups: [], error: '请选择有效的起止日期，结束日期不能早于开始日期' };
  }
  const keyword = query.keyword.trim().toLocaleLowerCase();
  const selected = rows.filter((row) => {
    if (keyword && !row.search.toLocaleLowerCase().includes(keyword)) return false;
    if (
      !Object.entries(query.selected).every(
        ([key, values]) => !values.length || values.includes(row.facets[key] || '未分配')
      )
    )
      return false;
    if (date.mode === 'ALL') return true;
    const day = beijingDay(row.dates[date.field]);
    return date.mode === 'MISSING' ? !day : Boolean(day && day >= date.from! && day <= date.to!);
  });
  const missing = (value: unknown) => value === undefined || value === null || value === '';
  selected.sort((a, b) => {
    const left = a.sort[query.sort.field],
      right = b.sort[query.sort.field];
    if (missing(left) !== missing(right)) return missing(left) ? 1 : -1;
    const order =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : String(left ?? '').localeCompare(String(right ?? ''), 'zh-CN');
    return (query.sort.direction === 'asc' ? order : -order) || a.id.localeCompare(b.id);
  });
  const groups = new Map<string, string[]>();
  if (query.groupBy) {
    for (const row of selected) {
      const key = row.facets[query.groupBy] || '未分配';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(row.id);
    }
  }
  return {
    ids: selected.map((row) => row.id),
    total: selected.length,
    groups: [...groups].map(([key, ids]) => ({ key, ids }))
  };
}
